import { AdvancedTableBlock } from '@bay-email/core';
import { IBoundingPosition, IOperationData } from './type';
import { getMaxTdCount } from './util';

type TableData = IOperationData[][];
type ChangeTableData = (data: AdvancedTableBlock['data']['value']['tableSource']) => void;

export function mergeCellsOp(
  tableData: TableData,
  boundary: IBoundingPosition,
  changeTableData: ChangeTableData,
) {
  const { top, left, bottom, right } = boundary;
  const leftTopItem = tableData[top].find(e => e.left === left) as IOperationData;
  leftTopItem.rowSpan = bottom - top + 1;
  leftTopItem.colSpan = right - left + 1;

  tableData.forEach((tr, trIndex) => {
    if (trIndex >= top && trIndex <= bottom) {
      if (bottom > top && trIndex > top && trIndex <= bottom) {
        leftTopItem.content += '<br />';
      }
      const deletedIndex: number[] = [];
      tr.forEach((td, tdIndex) => {
        if (top === trIndex && left === td.left) return;
        if (td.left >= left && td.right <= right) {
          leftTopItem.content += ' ' + td.content;
          deletedIndex.push(tdIndex);
        }
      });
      if (deletedIndex.length > 0) {
        tr.splice(deletedIndex[0], deletedIndex[deletedIndex.length - 1] - deletedIndex[0] + 1);
      }
    }
  });

  changeTableData(tableData as any);
}

export function deleteColumnsOp(
  tableData: TableData,
  boundary: IBoundingPosition,
  changeTableData: ChangeTableData,
) {
  const { left, right } = boundary;
  tableData.forEach(tr => {
    const deleteIds: number[] = [];
    for (let index = 0; index < tr.length; index++) {
      const td = tr[index];
      const tdLeft = td.left || 0;
      const tdRight = td.right || 0;
      const colSpan = td.colSpan || 1;
      if (tdLeft > right) break;
      if (tdLeft >= left && tdRight <= right) { deleteIds.push(index); continue; }
      if (tdLeft <= left && tdRight >= right) { td.colSpan = colSpan - (right - left) - 1; continue; }
      if (tdLeft > left && tdRight >= right) { td.colSpan = colSpan - (right - tdLeft) - 1; continue; }
      if (tdLeft < left && tdRight >= left) { td.colSpan = colSpan - (tdRight - left) - 1; continue; }
    }
    if (deleteIds.length) {
      tr.splice(deleteIds[0], deleteIds[deleteIds.length - 1] - deleteIds[0] + 1);
    }
  });
  changeTableData(tableData as any);
}

export function deleteRowsOp(
  tableData: TableData,
  boundary: IBoundingPosition,
  changeTableData: ChangeTableData,
) {
  const { top, bottom } = boundary;
  const deleteCount = bottom - top + 1;
  const maxTdCount = getMaxTdCount(tableData as any);

  // adjust rowSpan of cells above the deleted range that span into it
  for (let index = bottom - 1; index > -1; index--) {
    tableData[index].forEach(td => {
      if (td.bottom && td.bottom >= top) {
        const deleteRowSpan = td.bottom >= bottom ? deleteCount : td.bottom - top + 1;
        td.rowSpan = (td.rowSpan || 1) - deleteRowSpan;
      }
    });
  }

  // move cells that span beyond the deleted range into the next surviving row
  for (let index = top; index <= bottom; index++) {
    tableData[index].forEach(td => {
      const rowSpan = td.rowSpan || 1;
      const cellBottom = index + rowSpan - 1;
      if (cellBottom > bottom) {
        const overlappedDeletedRows = Math.min(cellBottom, bottom) - Math.max(index, top) + 1;
        const nextRowCell = { ...td, rowSpan: rowSpan - overlappedDeletedRows };
        const nextRow = tableData[bottom + 1];
        if (nextRow) {
          const insertIndex = nextRow.findIndex(cell => (cell.left ?? 0) > (nextRowCell.left ?? 0));
          if (insertIndex > -1) {
            nextRow.splice(insertIndex, 0, nextRowCell);
          } else {
            nextRow.push(nextRowCell);
          }
        }
      }
    });
  }

  tableData.splice(boundary.top, deleteCount);
  changeTableData(tableData as any);
}

export function insertColumnRightOp(
  tableData: TableData,
  boundary: IBoundingPosition,
  changeTableData: ChangeTableData,
) {
  const right = boundary.right;
  const maxTdCount = getMaxTdCount(tableData as any);
  tableData.forEach(tr => {
    if (right === maxTdCount - 1) { tr.push({ content: '-' } as any); return; }
    if (tr.length === 0) { tr.push({ content: '-' } as any); return; }
    for (let index = 0; index < tr.length; index++) {
      const tdLeft = tr[index].left || 0;
      const tdRight = tr[index].right || 0;
      if (tdRight === right) { tr.splice(index + 1, 0, { content: '-' } as any); break; }
      if (tdLeft <= right && tdRight > right && tr[index].colSpan) { tr[index].colSpan = (tr[index].colSpan || 1) + 1; break; }
      if (tdLeft > right && tdLeft - 1 === right) { tr.splice(index, 0, { content: '-' } as any); break; }
      if (tdLeft > right) break;
    }
  });
  changeTableData(tableData as any);
}

export function insertColumnLeftOp(
  tableData: TableData,
  boundary: IBoundingPosition,
  changeTableData: ChangeTableData,
) {
  const left = boundary.left;
  tableData.forEach(tr => {
    if (left === 0) { tr.unshift({ content: '-' } as any); return; }
    if (tr.length === 0) { tr.push({ content: '-' } as any); return; }
    for (let index = 0; index < tr.length; index++) {
      const tdLeft = tr[index].left || 0;
      const tdRight = tr[index].right || 0;
      if (tdLeft === left) { tr.splice(index, 0, { content: '-' } as any); break; }
      if (tdLeft < left && tdRight >= left && tr[index].colSpan) { tr[index].colSpan = (tr[index].colSpan || 1) + 1; break; }
      if (tdLeft > left && tr[index - 1] && (tr[index - 1].right || 0) + 1 === left) { tr.splice(index, 0, { content: '-' } as any); break; }
      if (tdLeft > left) break;
    }
  });
  changeTableData(tableData as any);
}

export function insertRowUpOp(
  tableData: TableData,
  boundary: IBoundingPosition,
  changeTableData: ChangeTableData,
) {
  const top = boundary.top;
  let addCount = getMaxTdCount(tableData as any);
  if (tableData[top].length < addCount) {
    for (let index = top - 1; index > -1; index--) {
      const tr = tableData[index];
      tr.forEach(td => {
        if (td.bottom && td.bottom >= top) {
          td.rowSpan = (td.rowSpan || 1) + 1;
          addCount -= td.colSpan || 1;
        }
      });
      if (tr.length === addCount) break;
    }
  }
  const newRow = Array.from({ length: addCount }).map(() => ({ content: '-' } as any));
  tableData.splice(top, 0, newRow);
  changeTableData(tableData as any);
}

export function insertRowDownOp(
  tableData: TableData,
  boundary: IBoundingPosition,
  changeTableData: ChangeTableData,
) {
  const bottom = boundary.bottom;
  const maxTdCount = getMaxTdCount(tableData as any);
  let addCount = maxTdCount;
  if (tableData[bottom].length < maxTdCount) {
    for (let index = bottom - 1; index > -1; index--) {
      const tr = tableData[index];
      if (tr.length === maxTdCount) break;
      tr.forEach(td => {
        if (td.bottom && td.bottom > bottom) {
          td.rowSpan = (td.rowSpan || 1) + 1;
          addCount -= td.colSpan || 1;
        }
      });
    }
  }
  tableData[bottom].forEach(e => {
    if (e.rowSpan && e.rowSpan > 1) {
      e.rowSpan += 1;
      addCount -= e.colSpan || 1;
    }
  });
  const newRow = Array.from({ length: addCount }).map(() => ({ content: '-' } as any));
  tableData.splice(bottom + 1, 0, newRow);
  changeTableData(tableData as any);
}
